// RBAC snapshot + helpers shared across the app.
//
// The backend (`/api/system/auth/whoami/`) returns
// `team_membership: { is_owner, owner_id, department, permissions: [codename...] }`.
// Owners short-circuit to `true` for every check.
//
// The backend codenames are namespaced per entity, e.g.
//   announcement.view_announcement, announcement.create_announcement
//   inventory_category.create_category, inventory_group.create_group
//   order.accept_order, cart.manage_cart, review.create_review
//
// Pages are gated by namespace ("does the user hold any `announcement.*`
// perm?") so a member with create-only access still sees the page. Buttons
// are gated by specific codename.

export interface RbacSnapshot {
    isOwner: boolean;
    ownerId: number | null;
    department: string | null;
    permissions: string[];
    lastSyncedAt: string | null;
}

export const EMPTY_RBAC: RbacSnapshot = {
    isOwner: false,
    ownerId: null,
    department: null,
    permissions: [],
    lastSyncedAt: null,
};

export function can(snapshot: RbacSnapshot | null | undefined, codename?: string | null): boolean {
    if (!codename) return true;
    if (!snapshot) return false;
    if (snapshot.isOwner) return true;
    return snapshot.permissions.includes(codename);
}

export function canAny(snapshot: RbacSnapshot | null | undefined, codenames: string[]): boolean {
    if (!codenames.length) return true;
    if (!snapshot) return false;
    if (snapshot.isOwner) return true;
    return codenames.some((c) => snapshot.permissions.includes(c));
}

export function canAll(snapshot: RbacSnapshot | null | undefined, codenames: string[]): boolean {
    if (!codenames.length) return true;
    if (!snapshot) return false;
    if (snapshot.isOwner) return true;
    return codenames.every((c) => snapshot.permissions.includes(c));
}

// True if the user holds any permission whose codename is `${namespace}.*`.
// Owners always pass.
export function hasNamespace(
    snapshot: RbacSnapshot | null | undefined,
    namespace: string,
): boolean {
    if (!snapshot) return false;
    if (snapshot.isOwner) return true;
    const prefix = `${namespace}.`;
    return snapshot.permissions.some((p) => p.startsWith(prefix));
}

export function hasAnyNamespace(
    snapshot: RbacSnapshot | null | undefined,
    namespaces: string[],
): boolean {
    if (!namespaces.length) return true;
    if (!snapshot) return false;
    if (snapshot.isOwner) return true;
    return namespaces.some((ns) => hasNamespace(snapshot, ns));
}

// Specific codenames used across the UI for button-level gating.
// Prefix-based namespaces are listed alongside (used by route guards and
// the sidebar so a user with create-only perms can still see the page).
export const PERMISSIONS = {
    // Team (rbac.txt + perms.txt)
    teamView: 'team.view_team',
    teamManageMembers: 'team.manage_team_members',
    teamManagePermissions: 'team.manage_team_permissions',

    // Department
    deptView: 'department.view_department',
    deptCreate: 'department.create_department',
    deptUpdate: 'department.edit_department',
    deptDelete: 'department.delete_department',

    // Announcements
    announcementView: 'announcement.view_announcement',
    announcementCreate: 'announcement.create_announcement',
    announcementEdit: 'announcement.edit_announcement',
    announcementDelete: 'announcement.delete_announcement',

    // Inventory — split per entity (note: products and units use bare
    // `product.*` / `unit.*` namespaces, while groups/categories sit under
    // `inventory_*`).
    inventoryProductView: 'product.view_product',
    inventoryProductCreate: 'product.create_product',
    inventoryProductEdit: 'product.edit_product',
    inventoryProductDelete: 'product.delete_product',
    inventoryCategoryCreate: 'inventory_category.create_category',
    inventoryCategoryEdit: 'inventory_category.edit_category',
    inventoryCategoryDelete: 'inventory_category.delete_category',
    inventoryGroupCreate: 'inventory_group.create_group',
    inventoryGroupEdit: 'inventory_group.edit_group',
    inventoryGroupDelete: 'inventory_group.delete_group',
    inventoryUnitCreate: 'unit.create_unit',
    inventoryUnitEdit: 'unit.edit_unit',
    inventoryUnitDelete: 'unit.delete_unit',

    // Orders
    orderView: 'order.view_order',
    orderCreate: 'order.create_order',
    orderSave: 'order.save_order',
    orderAccept: 'order.accept_order',
    orderReject: 'order.decline_order',
    orderModify: 'order.modify_order',
    orderCheckAvailability: 'order.check_order_availability',
    orderRequestReturn: 'order.request_return',
    orderApproveReturn: 'order.approve_return',

    // Quotations
    quotationView: 'quotation.view_quotation',
    quotationCreate: 'quotation.create_quotation',
    quotationRespond: 'quotation.respond_quotation',
    quotationReject: 'quotation.reject_quotation',

    // Related parties
    relatedPartyView: 'connection.view_connection',
    relatedPartyConnect: 'connection.send_connection',
    relatedPartyRemove: 'connection.delete_connection',
    relatedPartyManageFavourites: 'connection.manage_favourites',
    relatedPartyManageBlocks: 'connection.manage_blocks',

    // Cart
    cartView: 'cart.view_cart',
    cartManage: 'cart.manage_cart',

    // Reviews
    reviewView: 'review.view_review',
    reviewCreate: 'review.create_review',

    // Reports
    reportView: 'report.view_report',

    // WhatsApp
    whatsappView: 'whatsapp.view_conversation',
    whatsappSend: 'whatsapp.send_message',
    whatsappManage: 'whatsapp.manage_account',
} as const;

// Namespace constants used by route guard + sidebar (any perm in the
// namespace grants visibility).
export const NAMESPACES = {
    team: 'team',
    department: 'department',
    announcement: 'announcement',
    inventoryProduct: 'product',
    inventoryCategory: 'inventory_category',
    inventoryGroup: 'inventory_group',
    inventoryUnit: 'unit',
    order: 'order',
    quotation: 'quotation',
    relatedParty: 'connection',
    cart: 'cart',
    review: 'review',
    report: 'report',
    whatsapp: 'whatsapp',
} as const;

// Route prefix → namespaces. The user must hold at least one perm in any
// of the listed namespaces to access the route. Routes not listed here
// are open to every authenticated user (login guard still applies).
export const ROUTE_PERMISSIONS: { prefix: string; namespaces: string[] }[] = [
    { prefix: '/inventory/products/create', namespaces: [NAMESPACES.inventoryProduct] },
    { prefix: '/inventory/products', namespaces: [NAMESPACES.inventoryProduct] },
    { prefix: '/inventory/groups', namespaces: [NAMESPACES.inventoryGroup] },
    { prefix: '/inventory/categories', namespaces: [NAMESPACES.inventoryCategory] },
    { prefix: '/inventory/units', namespaces: [NAMESPACES.inventoryUnit] },
    {
        prefix: '/inventory',
        namespaces: [
            NAMESPACES.inventoryProduct,
            NAMESPACES.inventoryCategory,
            NAMESPACES.inventoryGroup,
            NAMESPACES.inventoryUnit,
        ],
    },
    { prefix: '/related-parties', namespaces: [NAMESPACES.relatedParty] },
    { prefix: '/respond-orders', namespaces: [NAMESPACES.order] },
    { prefix: '/orders', namespaces: [NAMESPACES.order] },
    { prefix: '/return', namespaces: [NAMESPACES.order] },
    { prefix: '/quotations', namespaces: [NAMESPACES.quotation] },
    { prefix: '/announcements', namespaces: [NAMESPACES.announcement] },
    { prefix: '/reports', namespaces: [NAMESPACES.report] },
    { prefix: '/cart', namespaces: [NAMESPACES.cart] },
    { prefix: '/chat', namespaces: [NAMESPACES.whatsapp] },
];

export function namespacesForPath(pathname: string): string[] {
    const match = ROUTE_PERMISSIONS.find((entry) => (
        pathname === entry.prefix || pathname.startsWith(`${entry.prefix}/`)
    ));
    return match ? match.namespaces : [];
}

// Shape of `team_membership` returned by `/auth/whoami/`.
interface WhoamiTeamMembership {
    is_owner?: boolean;
    owner_id?: number | null;
    department?: string | null;
    permissions?: string[];
}

export function snapshotFromWhoami(
    membership: WhoamiTeamMembership | null | undefined,
): RbacSnapshot {
    if (!membership) return EMPTY_RBAC;
    return {
        isOwner: !!membership.is_owner,
        ownerId: membership.owner_id ?? null,
        department: membership.department ?? null,
        permissions: Array.isArray(membership.permissions) ? membership.permissions : [],
        lastSyncedAt: new Date().toISOString(),
    };
}
